import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  Download,
  Plane,
  Users,
  UsersRound,
  Star,
} from "lucide-react";

const STATS = [
  {
    id: "downloads",
    icon: Download,
    value: 12400,
    suffix: "+",
    label: "DOWNLOADS",
    sub: "And climbing every day",
    tone: "#3056F5",
  },
  {
    id: "trips",
    icon: Plane,
    value: 3450,
    suffix: "+",
    label: "TRIPS TRACKED",
    sub: "From Goa to Manali",
    tone: "#F0EAFD",
  },
  {
    id: "groups",
    icon: UsersRound,
    value: 1820,
    suffix: "+",
    label: "GROUPS CREATED",
    sub: "Crews, flatmates, families",
    tone: "#3056F5",
  },
  {
    id: "users",
    icon: Users,
    value: 8700,
    suffix: "+",
    label: "TOTAL USERS",
    sub: "Across 42 Indian cities",
    tone: "#F0EAFD",
  },
  {
    id: "rating",
    icon: Star,
    value: 4.9,
    suffix: "★",
    decimals: 1,
    label: "AVERAGE RATING",
    sub: "220+ beta reviews",
    tone: "#F0EAFD",
  },
];

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function useCountUp(target, durationMs, run, decimals = 0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / durationMs);
      setVal(easeOutCubic(p) * target);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, run]);
  return decimals > 0
    ? val.toFixed(decimals)
    : Math.round(val).toLocaleString("en-IN");
}

function StatCard({ stat, index, run }) {
  const display = useCountUp(stat.value, 1800 + index * 120, run, stat.decimals || 0);
  const Icon = stat.icon;
  return (
    <motion.div
      data-testid={`stat-card-${stat.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.6,
        delay: index * 0.08,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -6 }}
      className="group relative bg-[#F0EAFD] hover:bg-white border border-[#F0EAFD]/20 hover:border-[#3056F5]/40 rounded-3xl p-7 md:p-9 overflow-hidden transition-colors"
    >
      <div className="flex items-center justify-between mb-6">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center bg-[#3056F5]"
        >
          <Icon className="w-5 h-5" color="#F0EAFD" />
        </div>
        <span className="text-[10px] font-bold tracking-[0.25em] text-[#0F2C5A]/40">
          0{index + 1}
        </span>
      </div>

      <div className="font-display leading-none text-[#0F2C5A] text-[clamp(2.6rem,5vw,4rem)] tabular-nums">
        {stat.prefix || ""}
        {display}
        <span className="text-[#3056F5]">{stat.suffix || ""}</span>
      </div>

      <div className="mt-5 pt-5 border-t border-[#0F2C5A]/10">
        <div className="text-[11px] font-bold tracking-[0.25em] text-[#0F2C5A]">
          {stat.label}
        </div>
        <div className="mt-1.5 text-sm text-[#0F2C5A]/60">{stat.sub}</div>
      </div>

      {/* hover glow */}
      <div className="pointer-events-none absolute -bottom-20 -right-20 w-48 h-48 rounded-full bg-[#3056F5]/0 group-hover:bg-[#3056F5]/15 blur-3xl transition-colors duration-500" />
    </motion.div>
  );
}

export default function Stats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });

  return (
    <section
      id="stats"
      data-testid="stats-section"
      ref={ref}
      className="relative bg-[#0F2C5A] text-[#F0EAFD] py-24 md:py-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#3056F5]/20 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute bottom-0 -right-40 w-[520px] h-[520px] rounded-full bg-[#F0EAFD]/5 blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14 md:mb-20">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-[#3056F5] font-bold tracking-[0.35em] text-xs mb-5"
          >
            BY THE NUMBERS
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display uppercase leading-[0.95] text-[clamp(2.2rem,5.5vw,4.5rem)]"
          >
            Crews settle smarter <br />
            <span className="text-[#3056F5]">with Qontri.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-5 text-[#F0EAFD]/70 max-w-xl mx-auto text-base md:text-lg"
          >
            Numbers from a fast-growing community of travel groups, flatmates,
            and families splitting expenses without the drama.
          </motion.p>
        </div>

        <div
          data-testid="stats-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
        >
          {STATS.map((s, i) => (
            <StatCard key={s.id} stat={s} index={i} run={inView} />
          ))}
        </div>

        <div className="mt-14 md:mt-20 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6">
          <span className="inline-flex items-center gap-2 text-[#F0EAFD]/60 text-xs tracking-[0.2em] font-bold">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#3056F5] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#3056F5]" />
            </span>
            UPDATED LIVE FROM PRODUCTION
          </span>
          <span className="hidden md:inline text-[#F0EAFD]/30">•</span>
          <span className="text-[#F0EAFD]/60 text-xs tracking-[0.2em] font-bold">
            DEC 2026
          </span>
        </div>
      </div>
    </section>
  );
}
